import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Flex, Heading, Text, Button, Card, Badge, TextArea, Callout } from "@radix-ui/themes";
import { ArrowUpIcon, PlusIcon, SparklesIcon } from "@heroicons/react/20/solid";
import { useAIAssistantSettings } from "hooks/aiAssistant/useAIAssistantSettings";
import { streamAIAssistantPlayground } from "api/aiAssistantPlayground";
import { AssistantLoadError } from "./AssistantStatus";
import "./AIAssistantPlayground.css";

function unavailable(settings) {
  if (settings?.sync_status === "pending" || settings?.sync_status === "error") return "Playground is unavailable until the Assistant is synchronized. Resolve provisioning or synchronization errors in Settings.";
  if (!settings?.enabled) return "Enable your organisation’s AI Assistant in Settings to use Playground.";
  if (settings.sync_status !== "synced") return "Playground is unavailable until the Assistant is synchronized. Resolve provisioning or synchronization errors in Settings.";
  if (!settings.public_assistant_id || settings.runtime_enabled !== true) return "The runtime is not confirmed enabled. Check synchronization in Settings.";
  return null;
}

export default function AIAssistantPlayground() {
  const query = useAIAssistantSettings();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [stateBlocked, setStateBlocked] = useState(false);
  const conversation = useRef(null);
  const active = useRef(null);
  const generation = useRef(0);
  const sequence = useRef(0);
  const messageArea = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      generation.current += 1;
      active.current?.abort();
      active.current = null;
    };
  }, []);

  useEffect(() => {
    if (messageArea.current) messageArea.current.scrollTop = messageArea.current.scrollHeight;
  }, [messages]);

  const reason = unavailable(query.data);
  useEffect(() => {
    if (!query.isPending && !query.isError && reason && active.current) {
      generation.current += 1;
      active.current.abort();
      active.current = null;
      setGenerating(false);
    }
  }, [query.isPending, query.isError, reason]);

  const newConversation = () => {
    generation.current += 1;
    active.current?.abort();
    active.current = null;
    conversation.current = null;
    setMessages([]);
    setInput("");
    setError("");
    setGenerating(false);
  };

  const send = async (event) => {
    event?.preventDefault();
    const message = input.trim();
    if (!message || message.length > 8000 || active.current || reason || stateBlocked) return;
    const controller = new AbortController();
    const current = ++generation.current;
    const assistantId = ++sequence.current;
    active.current = controller;
    setGenerating(true);
    setError("");
    setInput("");
    setMessages((previous) => [...previous, { id: ++sequence.current, role: "user", text: message },
      { id: assistantId, role: "assistant", text: "", status: "generating" }]);
    const isCurrent = () => mounted.current && current === generation.current && !controller.signal.aborted;
    try {
      await streamAIAssistantPlayground({ message, conversationId: conversation.current, signal: controller.signal,
        onEvent: (kind, data) => {
          if (!isCurrent()) return;
          if (kind === "start") conversation.current = data.conversation_id;
          if (kind === "text_delta") setMessages((previous) => previous.map((item) =>
            item.id === assistantId ? { ...item, text: item.text + data.text } : item));
          if (kind === "completed") setMessages((previous) => previous.map((item) =>
            item.id === assistantId ? { ...item, status: "completed" } : item));
        } });
    } catch (failure) {
      if (isCurrent() && failure.name !== "AbortError") {
        setError(failure.message || "The AI response could not be completed. Please try again.");
        setMessages((previous) => previous.map((item) => item.id === assistantId ? { ...item, status: "error" } : item));
        if (failure.code?.startsWith("assistant_") || failure.code === "runtime_not_enabled" || failure.code === "forbidden") {
          setStateBlocked(true);
          query.refetch().then(({ data, isError }) => { if (mounted.current && !isError && data && !unavailable(data)) setStateBlocked(false); });
        }
      }
    } finally {
      if (isCurrent()) {
        active.current = null;
        setGenerating(false);
      }
    }
  };

  if (query.isPending) return <Text role="status">Loading Playground…</Text>;
  if (query.isError) return <AssistantLoadError error={query.error} onRetry={query.refetch} />;
  return (
    <Flex direction="column" gap="4" className="ai-playground">
      <Flex justify="between" align="start" gap="4" wrap="wrap">
        <Flex direction="column" gap="2">
          <Heading size="6">Playground</Heading>
          <Text size="2" color="gray">Test your organisation’s central Assistant in a new conversation.</Text>
        </Flex>
        <Button variant="soft" onClick={newConversation}><PlusIcon width={16} />New conversation</Button>
      </Flex>
      <Flex align="center" gap="2"><Badge color="gray">Test conversation</Badge><Text size="1" color="gray">Shared Assistant · organisation-wide</Text></Flex>
      {reason || stateBlocked ? <Card><Flex direction="column" gap="3">
        <Heading size="3">Playground unavailable</Heading>
        <Text>{reason || "Assistant access could not be confirmed. Check Settings before continuing."}</Text>
        <Link to="/ai-assistant/settings">Open AI Assistant Settings</Link>
        <Button variant="soft" onClick={async () => {
          const result = await query.refetch();
          if (!result.isError && result.data && !unavailable(result.data)) setStateBlocked(false);
        }}>Refresh Assistant status</Button>
      </Flex></Card> : <>
        <div className="ai-playground-messages" role="log" aria-label="Playground messages" aria-live="off" ref={messageArea}>
          {messages.length === 0 ? <Flex direction="column" align="center" justify="center" gap="3" className="ai-playground-empty">
            <SparklesIcon width={32} aria-hidden="true" />
            <Heading size="4">Start a test conversation</Heading>
            <Text size="2" color="gray" align="center">Send a message to try your Assistant. Follow-up messages use the context of this session.</Text>
          </Flex> : messages.map((item) => <article key={item.id} className={`ai-playground-message ai-playground-message-${item.role}`} aria-label={item.role === "user" ? "Your message" : "Assistant message"}>
            <Text size="1" weight="medium" color="gray">{item.role === "user" ? "You" : "Assistant"}</Text>
            <p>{item.text || (item.status === "error" ? "No response received." : item.status === "completed" ? "The Assistant returned no text." : "Preparing response…")}</p>
            {item.status === "error" && item.text && <Text size="1" color="red">Incomplete response</Text>}
          </article>)}
        </div>
        {error && <Callout.Root color="red" role="alert"><Callout.Text>{error}</Callout.Text></Callout.Root>}
        <form onSubmit={send} className="ai-playground-composer">
          <TextArea aria-label="Message" placeholder="Write a message to your Assistant…" value={input} rows={3} maxLength={8000}
            disabled={generating} onChange={(event) => setInput(event.target.value)} onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); send(); }
            }} />
          <Flex justify="between" align="center" gap="3" mt="3">
            <Text size="1" color="gray" role="status">{generating ? "Generating response…" : "Enter to send · Shift+Enter for a new line"}</Text>
            <Button type="submit" disabled={generating || !input.trim()}><ArrowUpIcon width={16} />{generating ? "Generating…" : "Send message"}</Button>
          </Flex>
        </form>
      </>}
    </Flex>
  );
}
